import { flushSync } from "react-dom";
import {
  Button as RACButton,
  Text,
  UNSTABLE_Toast as RACToast,
  UNSTABLE_ToastContent as ToastContent,
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastRegion as ToastRegion,
} from "react-aria-components";
import { Icon, IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconX } from "../icon";
import { useStrings } from "../strings";
import { cx } from "../tv";

export type ToastContentData = {
  title: string;
  description?: string;
  tone?: "neutral" | "success" | "danger" | "info";
  /** One action, usually Undo for a destructive change (DESIGN-RULES 9). */
  action?: { label: string; onAction: () => void };
};

/** At most three visible, bottom right (bottom centre on phones). Toasts announce through aria-live. */
export const toastQueue = new ToastQueue<ToastContentData>({
  maxVisibleToasts: 3,
  wrapUpdate(fn) {
    if ("startViewTransition" in document && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      (document as Document & { startViewTransition: (cb: () => void) => void }).startViewTransition(() => flushSync(fn));
    } else fn();
  },
});

export function toast(content: ToastContentData, opts: { timeout?: number } = {}) {
  return toastQueue.add(content, { timeout: content.action ? opts.timeout ?? 7000 : opts.timeout ?? 5000 });
}

const TONE_ICON = { neutral: null, success: IconCircleCheck, danger: IconAlertTriangle, info: IconInfoCircle } as const;
const TONE_CLS = { neutral: "text-fg-muted", success: "text-success", danger: "text-danger", info: "text-info" } as const;

export function Toaster() {
  const t = useStrings();
  return (
    <ToastRegion queue={toastQueue} className="fixed bottom-4 end-4 z-(--z-toast) flex w-[min(380px,calc(100vw-32px))] flex-col-reverse gap-2 outline-none max-tablet:end-1/2 max-tablet:translate-x-1/2 max-tablet:bottom-[calc(var(--tabbar-h)+12px)]">
      {({ toast: item }) => {
        const tone = item.content.tone ?? "neutral";
        const ToneIcon = TONE_ICON[tone];
        return (
          <RACToast
            toast={item}
            style={{ viewTransitionName: item.key }}
            className="flex items-start gap-3 rounded-lg bg-panel-solid px-4 py-3 shadow-pop outline-none focus-visible:shadow-focus"
          >
            {ToneIcon && <Icon icon={ToneIcon} className={cx("mt-px", TONE_CLS[tone])} />}
            <ToastContent className="flex min-w-0 flex-1 flex-col">
              <Text slot="title" className="text-base font-semibold text-fg">{item.content.title}</Text>
              {item.content.description && <Text slot="description" className="text-sm text-fg-muted">{item.content.description}</Text>}
            </ToastContent>
            {item.content.action && (
              <RACButton
                onPress={() => { item.content.action!.onAction(); toastQueue.close(item.key); }}
                className="-my-1 h-7 shrink-0 rounded-sm px-2 text-sm font-semibold text-link outline-none hovered:bg-hover focus-visible:shadow-focus"
              >
                {item.content.action.label}
              </RACButton>
            )}
            <RACButton slot="close" aria-label={t.dismiss} className="-me-1 -my-0.5 grid size-6 shrink-0 place-items-center rounded-sm text-fg-subtle outline-none hovered:bg-hover focus-visible:shadow-focus">
              <Icon icon={IconX} size="chip" />
            </RACButton>
          </RACToast>
        );
      }}
    </ToastRegion>
  );
}
